import { useCallback } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';

import useFormData from '@gef-ui/form-core/hooks/state/useFormData';
import Autocomplete from '@gef-ui/form-material/Autocomplete';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { WalkingTransitionIconSet } from 'components/atoms/CustomSVGIcon';
import codelistProvider from 'components/decorators/codelistProvider';
import { codelists } from 'constants/codelists';
import { generateFromAndToColorForWalkingIcon } from 'features/views/semaphore/utils';

const ImpendingStateAutoComplete = ({ impendingStateCodelist, fieldName }) => {
	const formData = useFormData();

	const getOptionLabel = useCallback((option) => option.label, []);

	const renderOption = useCallback((props, option) => {
		const result = generateFromAndToColorForWalkingIcon(option.value);
		return (
			<Box component="li" {...props}>
				<Box sx={{ fontSize: '20px', mr: '20px', mt: 1 }}>
					<WalkingTransitionIconSet fromColor={result?.fromColor} toColor={result?.toColor} />
				</Box>
				{option.label}
			</Box>
		);
	}, []);

	const renderInput = useCallback(
		(params) => {
			const result = generateFromAndToColorForWalkingIcon(formData?.impendingState);
			const startAdornment = (
				<InputAdornment position="start" sx={{ ml: 1 }}>
					<Box sx={{ fontSize: '20px', mt: 1 }}>
						<WalkingTransitionIconSet fromColor={result?.fromColor} toColor={result?.toColor} />
					</Box>
				</InputAdornment>
			);

			return (
				<TextField
					{...params}
					label={getLabelMessage('semaphore:impendingState')}
					fullWidth
					slotProps={{
						input: {
							...params?.InputProps,
							...(formData?.impendingState && { startAdornment }),
						},
					}}
				/>
			);
		},
		[formData?.impendingState],
	);

	return (
		<Autocomplete
			size="small"
			fieldName={fieldName}
			label={getLabelMessage('semaphore:impendingState')}
			options={impendingStateCodelist}
			labelPropName="description"
			getOptionLabel={getOptionLabel}
			renderOption={renderOption}
			renderInput={renderInput}
		/>
	);
};

ImpendingStateAutoComplete.propTypes = {
	fieldName: PropTypes.string,
	impendingStateCodelist: PropTypes.array,
};

export default codelistProvider(codelists.semaphorePrediction, 'impendingStateCodelist')(ImpendingStateAutoComplete);
